import React, { useState, useEffect } from 'react';
import { Form, Button, Icon, Message, Input, Modal } from 'semantic-ui-react';
import SuccessModal from '../../../components/SuccessModal';
import ErrorModal from '../../../components/ErrorModal';
import CreationLogsModal from '../../../components/CreationLogsModal';
import VMCreationErrorModal from '../../../components/VMCreationErrorModal';
import axios from 'axios';

const RequestForm3 = ({
    onSubmit,
    userEmail,
    roles,
    categories = [],
    initialValues = null,
    originalCloudName = null
}) => {
    const [formData, setFormData] = useState({
        tenant: '1', // Dynamic based on category: *.parablu.com for DEV/QA, *.crashplan.com for STAGE/PROD
        location: '1', // Fixed to Central India
        vmSize: '2', // Default to 4vCPU-16GB RAM
        cloudName: '',
        storageDestination: '', // User selects Azure Blob, CPG DataCenter, or BYOS
        category: '', // No default category
        diskSizeGB: '512', // Default to 512GB
        azureBlobHosted: false, // Set based on storage destination selection
        azureBlobFuse: true, // Hidden but always true
        hasLicenseFile: false, // Default to no
        licenseFile: null,
        expiryDays: 7, // Default to 7 days (used when migration is enabled)
        tenantInfo: null,
        isMultiTenancySetup: true, // Fixed to Multi Tenancy Setup
        isMigrationSetup: false, // Migration toggle
        oldTenantUrl: '', // New field for migration
        migrationCompletionDate: '', // Date and time for migration completion
        isPOC: false // POC checkbox for PROD category
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [allRequests, setAllRequests] = useState([]);
    const [formCategory, setFormCategory] = useState('');
    const [showAutoProvisionVerifyModal, setShowAutoProvisionVerifyModal] = useState(false);
    const [pendingAutoProvisionData, setPendingAutoProvisionData] = useState(null);
    const [autoProvisionInfo, setAutoProvisionInfo] = useState({
        enabled: false,
        categories: []
    });
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [creationLogs, setCreationLogs] = useState([]);
    const [isCancelled, setIsCancelled] = useState(false);
    const [abortController, setAbortController] = useState(null);
    const [vmCreationError, setVMCreationError] = useState(null);
    const [showVMCreationErrorModal, setShowVMCreationErrorModal] = useState(false);

    // Tenant options - dynamically shown based on category
    const tenantOptions = [
        {
            key: '1',
            text: '*.parablu.com',
            value: '1',
            actual: '*.parablu.com',
            azureTenantId: process.env.REACT_APP_PARABLU_TENANT_ID,
            subscriptionId: process.env.REACT_APP_MACC_SUBSCRIPTION_ID,
            azureClientId: process.env.REACT_APP_PARABLU_CLIENT_ID
        },
        {
            key: '4',
            text: '*.crashplan.com',
            value: '4',
            actual: '*.crashplan.com',
            azureTenantId: process.env.REACT_APP_PARABLU_TENANT_ID,
            subscriptionId: process.env.REACT_APP_MACC_SUBSCRIPTION_ID,
            azureClientId: process.env.REACT_APP_PARABLU_CLIENT_ID
        }
    ];

    const locationOptions = [
        {
            key: '1',
            text: 'Central India',
            value: '1',
            urlPrefix: 'prod-portal-in',
            details: {
                location: process.env.REACT_APP_CI_LOCATION,
                resourceGroupName: process.env.REACT_APP_CI_RESOURCE_GROUP_NAME,
                vnetName: process.env.REACT_APP_CI_VNET_NAME,
                subnetName: process.env.REACT_APP_CI_SUBNET_NAME,
                nsgName: process.env.REACT_APP_CI_NSG_NAME
            }
        },
        {
            key: '2',
            text: 'East US',
            value: '2',
            urlPrefix: 'prod-portal-use',
            details: {
                location: process.env.REACT_APP_EUS_LOCATION,
                resourceGroupName: process.env.REACT_APP_EUS_RESOURCE_GROUP_NAME,
                vnetName: process.env.REACT_APP_EUS_VNET_NAME,
                subnetName: process.env.REACT_APP_EUS_SUBNET_NAME,
                nsgName: process.env.REACT_APP_EUS_NSG_NAME
            }
        },
        // {
        //     key: '3',
        //     text: 'Germany',
        //     value: '3',
        //     urlPrefix: 'prod-portal-ffm',
        //     details: {
        //         location: process.env.REACT_APP_GERMANY_LOCATION,
        //         resourceGroupName: process.env.REACT_APP_GERMANY_RESOURCE_GROUP_NAME,
        //         vnetName: process.env.REACT_APP_GERMANY_VNET_NAME,
        //         subnetName: process.env.REACT_APP_GERMANY_SUBNET_NAME,
        //         nsgName: process.env.REACT_APP_GERMANY_NSG_NAME
        //     }
        // },
        {
            key: '4',
            text: 'UAE North',
            value: '4',
            urlPrefix: 'prod-portal-uae',
            details: {
                location: process.env.REACT_APP_UAE_LOCATION,
                resourceGroupName: process.env.REACT_APP_UAE_RESOURCE_GROUP_NAME,
                vnetName: process.env.REACT_APP_UAE_VNET_NAME,
                subnetName: process.env.REACT_APP_UAE_NSG_NAME,
                nsgName: process.env.REACT_APP_UAE_SUBNET_NAME
            }
        }
    ];

    const storageOptions = [
        { key: '1', text: 'Azure Blob', value: '1', actual: 'Azure_Blob', azureBlobHosted: true },
        { key: '2', text: 'CPG DataCenter', value: '2', actual: 'cpg_datacenter', azureBlobHosted: false },
        { key: '3', text: 'BYOS (S3-Compatible)', value: '3', actual: 'BYOS', azureBlobHosted: false }
    ];

    const resolveCategoryValue = (rawCategory) => {
        if (!rawCategory) return '';
        const normalized = rawCategory.toString().trim().toUpperCase();
        const exactMatch = categories.find(cat => cat === normalized);
        if (exactMatch) return exactMatch;
        const caseInsensitiveMatch = categories.find(
            cat => cat && cat.toString().trim().toUpperCase() === normalized
        );
        return caseInsensitiveMatch || normalized;
    };

    // Transform categories prop into dropdown options
    // Filter to only show standard multi-tenancy categories if they exist in the categories prop
    const multiTenancyCategories = ['ENGG_DEV', 'ENGG_QA', 'STAGE', 'PROD'];
    const categoryOptions = categories
        .filter(cat => multiTenancyCategories.includes(cat))
        .map(category => ({
            key: category,
            text: category === 'PROD' ? 'PROD / POC' : category,
            value: category
        }));


    // Fetch auto provisioning access for current user
    useEffect(() => {
        const fetchAutoProvisionInfo = async () => {
            if (!userEmail) return;
            try {
                const response = await axios.get('https://qa-tenantbridge.parablu.com/api/user/roles', {
                    withCredentials: true,
                    timeout: 5000
                });
                // RequestForm3 is always multi-tenancy, so use multi-tenancy fields
                console.log('Auto-provision info fetched:', {
                    enabled: response.data.autoProvisionMultiTenancyEnabled,
                    categories: response.data.autoProvisionMultiTenancyCategories
                });
                setAutoProvisionInfo({
                    enabled: !!response.data.autoProvisionMultiTenancyEnabled,
                    categories: response.data.autoProvisionMultiTenancyCategories || []
                });
            } catch (err) {
                console.error('Error fetching auto-provision info:', err);
                setAutoProvisionInfo({
                    enabled: false,
                    categories: []
                });
            }
        };

        fetchAutoProvisionInfo();
    }, [userEmail]);

    const hasAutoProvisionForSelectedCategory =
        autoProvisionInfo.enabled &&
        formData.category &&
        Array.isArray(autoProvisionInfo.categories) &&
        autoProvisionInfo.categories.some(
            (c) =>
                c &&
                c.trim().toUpperCase() === formData.category.trim().toUpperCase()
        );

    // Debug logging
    useEffect(() => {
        if (formData.category) {
            console.log('Category selected:', formData.category);
            console.log('Auto-provision enabled:', autoProvisionInfo.enabled);
            console.log('Auto-provision categories:', autoProvisionInfo.categories);
            console.log('Has auto-provision for category:', hasAutoProvisionForSelectedCategory);
        }
    }, [formData.category, autoProvisionInfo.enabled, autoProvisionInfo.categories, hasAutoProvisionForSelectedCategory]);

    useEffect(() => {
        // Auto-select location and tenant based on category
        if (formData.category === 'STAGE') {
            setFormData(prev => ({
                ...prev,
                location: '2', // East US for STAGE
                tenant: '4' // *.crashplan.com for STAGE
            }));
        } else if (formData.category === 'PROD') {
            setFormData(prev => ({
                ...prev,
                location: prev.location || '2', // Keep current location or default to East US for PROD
                tenant: '4' // *.crashplan.com for PROD
            }));
        } else if (formData.category === 'ENGG_DEV') {
            setFormData(prev => ({
                ...prev,
                location: '1', // Central India for DEV
                tenant: '1' // *.parablu.com for DEV
            }));
        } else if (formData.category === 'ENGG_QA') {
            setFormData(prev => ({
                ...prev,
                // QA now supports Central India and East US; preserve selection if already valid
                location: ['1', '2'].includes(prev.location) ? prev.location : '1',
                tenant: '1' // *.parablu.com for QA
            }));
        }
    }, [formData.category]);

    const extractCloudNameFromUrl = (url) => {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const pathParts = pathname.split('/').filter(part => part.length > 0);
            return pathParts[0] || '';
        } catch (error) {
            return '';
        }
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'oldTenantUrl':
                if (formData.isMigrationSetup) {
                    if (!value) return 'Old tenant URL is required for migration';

                    // Check if it's a valid HTTPS URL
                    try {
                        const url = new URL(value);
                        if (url.protocol !== 'https:') {
                            return 'URL must use HTTPS protocol';
                        }

                        // Check if URL has a pathname with cloudname
                        const pathname = url.pathname;
                        const pathParts = pathname.split('/').filter(part => part.length > 0);

                        if (pathParts.length === 0) {
                            return 'URL must contain a customer name after the domain (e.g., https://domain.com/customername)';
                        }

                        const extractedCloudName = pathParts[0];
                        if (extractedCloudName.length < 3) {
                            return 'Customer name in URL must be at least 3 characters';
                        }

                        return '';
                    } catch (error) {
                        return 'Please enter a valid HTTPS URL (e.g., https://qa2-portal-in.parablu.com/example)';
                    }
                }
                return '';

            case 'cloudName':
                if (!formData.isMigrationSetup) {
                    if (!value) return 'Customer name is required';
                }
                return '';

            case 'storageDestination':
                if (!formData.isMigrationSetup) {
                    if (!value) return 'Storage destination is required';
                }
                return '';

            case 'licenseFile':
                if (formData.hasLicenseFile) {
                    if (!value) return 'License key file is required';
                    if (!value.name.endsWith('.pblic')) return 'File must be a .pblic file';
                }
                return '';

            case 'migrationCompletionDate':
                // No validation required - optional field with default value
                return '';

            default:
                return '';
        }
    };

    const validateCloudName = (cloudName) => {
        if (!cloudName) return '';

        // Skip validation if migration setup is enabled
        if (formData.isMigrationSetup) {
            return '';
        }

        if (!allRequests || allRequests.length === 0) return '';

        // Skip validation if editing the same request (cloud name hasn't changed)
        if (originalCloudName && cloudName.toLowerCase() === originalCloudName.toLowerCase()) {
            return '';
        }

        const existingRequest = allRequests.find(request => {
            const status = request?.status?.toLowerCase() || '';
            const isReusableStatus =
                status.includes('failed-vm creation') ||
                status.includes('withdrawn');

            return request?.formData?.cloudName?.toLowerCase() === cloudName.toLowerCase() &&
                !isReusableStatus;
        });

        if (existingRequest) {
            return `Customer name "${cloudName}" already exists and is in ${existingRequest.status.toLowerCase()}`;
        }
        return '';
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e, { name, value, checked }) => {
        if (name === 'oldTenantUrl') {
            // Extract cloud name from old tenant URL for migration
            const extractedCloudName = formData.isMigrationSetup && value ? extractCloudNameFromUrl(value) : '';
            setFormData(prev => ({
                ...prev,
                [name]: value,
                cloudName: extractedCloudName || prev.cloudName
            }));
            const error = validateField('oldTenantUrl', value);
            setErrors(prev => ({ ...prev, oldTenantUrl: error }));
        } else if (name === 'storageDestination') {
            // Find the selected storage option
            const selectedOption = storageOptions.find(opt => opt.value === value);
            // Set azureBlobHosted automatically based on the selection
            setFormData(prev => ({
                ...prev,
                [name]: value,
                azureBlobHosted: selectedOption ? selectedOption.azureBlobHosted : false
            }));
            const error = validateField('storageDestination', value);
            setErrors(prev => ({ ...prev, storageDestination: error }));
        } else if (name === 'category') {
            // When category changes, reset isPOC if not PROD
            setFormData(prev => ({
                ...prev,
                [name]: value,
                isPOC: value === 'PROD' ? prev.isPOC : false
            }));
            const error = validateField('category', value);
            setErrors(prev => ({ ...prev, category: error }));
        } else if (name === 'isPOC') {
            // Handle checkbox change
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'cloudName') {
            // Only allow manual editing if migration is NOT enabled
            setFormData(prev => ({ ...prev, [name]: value }));
            if (!formData.isMigrationSetup) {
                const error = validateField('cloudName', value);
                setErrors(prev => ({ ...prev, cloudName: error }));
            }
        } else if (name === 'migrationCompletionDate') {
            setFormData(prev => ({ ...prev, [name]: value }));
            const error = validateField('migrationCompletionDate', value);
            setErrors(prev => ({ ...prev, migrationCompletionDate: error }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        try {
            setSubmitting(true);
            setErrors({});

            const selectedTenant = tenantOptions.find(opt => opt.value === formData.tenant);
            const selectedStorage = storageOptions.find(opt => opt.value === formData.storageDestination);

            // Map tenant value to backend tenantKey
            const tenantKeyMap = {
                '1': 'parablu',   // *.parablu.com maps to 'parablu' backend key
                '4': 'crashplan'  // *.crashplan.com maps to 'crashplan' backend key
            };
            const tenantKey = tenantKeyMap[formData.tenant] || 'parablu';

            const transformedData = {
                tenant: selectedTenant?.actual,
                location: locationOptions.find(loc => loc.value === formData.location)?.details,
                vmSize: 'Standard_D4as_v5',
                cloudName: formData.cloudName.toLowerCase(),
                storageDestination: selectedStorage?.actual,
                category: formData.category,
                diskSizeGB: 512,
                azureBlobFuse: formData.azureBlobFuse,
                azureBlobHosted: formData.azureBlobHosted,
                submittedBy: userEmail,
                submittedAt: new Date().toISOString(),
                hasLicenseFile: formData.hasLicenseFile,
                licenseFile: formData.hasLicenseFile ? formData.licenseFile : null,
                expiryDays: null,
                azureTenantId: selectedTenant?.azureTenantId,
                subscriptionId: selectedTenant?.subscriptionId,
                tenantKey: tenantKey,
                subscriptionName: 'MACC May 2023 - April 2028 Subscription',
                tenantInfo: {
                    id: selectedTenant?.azureTenantId,
                    name: selectedTenant?.text,
                    subscriptionId: selectedTenant?.subscriptionId,
                    subscriptionName: 'MACC May 2023 - April 2028 Subscription'
                },
                isMultiTenancySetup: formData.isMultiTenancySetup,
                isMigrationSetup: formData.isMigrationSetup,
                oldTenantUrl: formData.isMigrationSetup ? formData.oldTenantUrl : null,
                migrationCompletionDate: formData.isMigrationSetup ? (formData.migrationCompletionDate || '2027-02-25T13:00') : null,
                isPOC: formData.isPOC || false,
                requestForm3: true, // Flag to identify this as Multi Tenancy Form
                autoProvision: hasAutoProvisionForSelectedCategory
            };

            // If auto provisioning applies, show verification modal first
            if (hasAutoProvisionForSelectedCategory) {
                setPendingAutoProvisionData(transformedData);
                setShowAutoProvisionVerifyModal(true);
                return;
            }

            setFormCategory(transformedData.category);
            const result = await onSubmit(transformedData);

            if (result.success) {
                    setShowSuccessModal(true);
                    setFormData({
                        tenant: '1',
                        location: '1',
                        vmSize: '2',
                        cloudName: '',
                        storageDestination: '',
                        category: '',
                        diskSizeGB: '512',
                        azureBlobHosted: false,
                        azureBlobFuse: true,
                        hasLicenseFile: false,
                        licenseFile: null,
                        expiryDays: 7,
                        tenantInfo: null,
                        isMultiTenancySetup: true,
                        isMigrationSetup: false,
                        oldTenantUrl: '',
                        migrationCompletionDate: '',
                        isPOC: false
                    });
            } else {
                setSubmitError(result.error || 'Failed to submit request');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Submit error:', error);
            setSubmitError(error.message || 'An unexpected error occurred');
            setShowErrorModal(true);
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmAutoProvisionSubmit = async () => {
        if (!pendingAutoProvisionData) return;
        try {
            setSubmitting(true);
            setShowAutoProvisionVerifyModal(false);
            setFormCategory(pendingAutoProvisionData.category);
            
            // Submit directly to API to get requestId
            const submitResponse = await axios.post('https://qa-tenantbridge.parablu.com/api/submit-request', {
                formData: {
                    ...pendingAutoProvisionData,
                    submittedBy: userEmail,
                    submittedAt: new Date().toISOString()
                }
            }, { withCredentials: true });

            const result = {
                success: submitResponse.data.success,
                message: submitResponse.data.message,
                requestId: submitResponse.data.requestId
            };

            console.log('Auto-provision Multi Tenancy Form submit result:', result);

            if (result.success && result.requestId) {
                // Check if this is a migration request - migration requests require DEVOPS approval
                // even if auto-provisioning is enabled, so we skip the auto-approval step
                const isMigrationRequest = !!(pendingAutoProvisionData.isMigrationSetup);
                
                if (isMigrationRequest) {
                    // For migration requests, just show success message - DEVOPS approval is required
                    setShowSuccessModal(true);
                    setFormData({
                        tenant: '1',
                        location: '1',
                        vmSize: '2',
                        cloudName: '',
                        storageDestination: '',
                        category: '',
                        diskSizeGB: '512',
                        azureBlobHosted: false,
                        azureBlobFuse: true,
                        hasLicenseFile: false,
                        licenseFile: null,
                        tenantInfo: null,
                        isMultiTenancySetup: true,
                        isMigrationSetup: false,
                        oldTenantUrl: ''
                    });
                    return;
                }

                // For auto-provision requests (non-migration), fetch the request and start approval
                // Wait a moment for the request to be saved in the database
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                try {
                    console.log('Fetching auto-provision request for Multi Tenancy Form:', result.requestId);
                    // Fetch the auto-provision request
                    const response = await axios.get(
                        `https://qa-tenantbridge.parablu.com/api/auto-provision/request/${result.requestId}`,
                        { withCredentials: true }
                    );

                    console.log('Auto-provision request response:', response.data);

                    if (response.data.success && response.data.request) {
                        // For Multi Tenancy Form, directly start the approval process with streaming logs
                        console.log('Starting Multi Tenancy Form approval process');
                        
                        // Open logs modal and start streaming
                        setShowLogsModal(true);
                        setCreationLogs([]);
                        setIsCancelled(false);
                        setCreationLogs(prev => [...prev, 'Starting Multi Tenancy Form processing...']);
                        setCreationLogs(prev => [...prev, `Customer Name: ${response.data.request.formData.cloudName}`]);
                        setCreationLogs(prev => [...prev, `Category: ${response.data.request.category}`]);

                        const controller = new AbortController();
                        const signal = controller.signal;
                        setAbortController(controller);

                        const heartbeatInterval = setInterval(() => {
                            setCreationLogs(prev => [...prev, `Still processing... (${new Date().toLocaleTimeString()})`]);
                        }, 60000);

                        try {
                            const approveResponse = await fetch('https://qa-tenantbridge.parablu.com/api/approve-form3', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Cache-Control': 'no-cache',
                                    'Connection': 'keep-alive',
                                    'Keep-Alive': 'timeout=600'
                                },
                                body: JSON.stringify({
                                    requestId: response.data.request.id,
                                    approverEmail: userEmail
                                }),
                                credentials: 'include',
                                signal,
                                keepalive: true
                            });

                            if (!approveResponse.ok) {
                                throw new Error(`HTTP error! status: ${approveResponse.status}`);
                            }

                            const reader = approveResponse.body.getReader();
                            const decoder = new TextDecoder();
                            let buffer = '';

                            while (true) {
                                try {
                                    const { value, done } = await reader.read();

                                    if (done) {
                                        console.log('Multi Tenancy Form stream completed normally');
                                        clearInterval(heartbeatInterval);
                                        break;
                                    }

                                    const chunk = decoder.decode(value, { stream: true });
                                    buffer += chunk;

                                    const lines = buffer.split('\n');
                                    buffer = lines.pop() || '';

                                    for (const line of lines) {
                                        if (line.trim()) {
                                            try {
                                                const data = JSON.parse(line);

                                                if (data.type === 'log') {
                                                    setCreationLogs(prev => [...prev, `${data.message}`]);
                                                } else if (data.type === 'heartbeat') {
                                                    console.log('Heartbeat received:', data.message);
                                                } else if (data.type === 'error') {
                                                    setVMCreationError({
                                                        errorLocation: data.errorLocation || 'Multi Tenancy Form Processing',
                                                        error: data.message,
                                                        requestId: data.requestId || response.data.request.id,
                                                        cloudName: data.cloudName || response.data.request.formData?.cloudName,
                                                        cloudNameLabel: 'Customer Name'
                                                    });
                                                    setShowVMCreationErrorModal(true);
                                                    setCreationLogs(prev => [...prev, `❌ Error: ${data.message}`]);
                                                }
                                            } catch (parseError) {
                                                console.warn('Failed to parse log line:', line, parseError);
                                            }
                                        }
                                    }
                                } catch (readError) {
                                    if (readError.name === 'AbortError') {
                                        console.log('Stream aborted by user');
                                        clearInterval(heartbeatInterval);
                                        break;
                                    }
                                    throw readError;
                                }
                            }

                            // Reset form after successful completion
                            setFormData({
                                tenant: '1',
                                location: '1',
                                vmSize: '2',
                                cloudName: '',
                                storageDestination: '',
                                category: '',
                                diskSizeGB: '512',
                                azureBlobHosted: false,
                                azureBlobFuse: true,
                                hasLicenseFile: false,
                                licenseFile: null,
                                tenantInfo: null,
                                isMultiTenancySetup: true,
                                isMigrationSetup: false,
                                oldTenantUrl: ''
                            });

                        } catch (streamError) {
                            clearInterval(heartbeatInterval);
                            if (streamError.name !== 'AbortError') {
                                console.error('Error streaming Multi Tenancy Form approval:', streamError);
                                setVMCreationError({
                                    errorLocation: 'Multi Tenancy Form Processing',
                                    error: streamError.message,
                                    requestId: response.data.request.id,
                                    cloudName: response.data.request.formData?.cloudName,
                                    cloudNameLabel: 'Customer Name'
                                });
                                setShowVMCreationErrorModal(true);
                            }
                        } finally {
                            setAbortController(null);
                        }
                    } else {
                        throw new Error('Failed to fetch auto-provision request');
                    }
                } catch (fetchError) {
                    console.error('Error processing auto-provision for Multi Tenancy Form:', fetchError);
                    setSubmitError('Failed to process request. Please try again.');
                    setShowErrorModal(true);
                }
            } else if (result.success) {
                // If no requestId, this is an error for auto-provision flow
                console.error('Auto-provision Multi Tenancy Form request submitted but no requestId returned');
                setSubmitError('Request submitted but failed to get request ID. Please check "My Submitted Requests" to approve manually.');
                setShowErrorModal(true);
            } else {
                setSubmitError(result.error || 'Failed to submit request');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Submit error (auto provision confirm):', error);
            setSubmitError(error.message || 'An unexpected error occurred');
            setShowErrorModal(true);
        } finally {
            setSubmitting(false);
            setPendingAutoProvisionData(null);
        }
    };

    const handleClear = () => {
        setFormData({
            tenant: '1',
            location: '1',
            vmSize: '2',
            cloudName: '',
            storageDestination: '',
            category: '',
            diskSizeGB: '512',
            azureBlobHosted: false,
            azureBlobFuse: true,
            hasLicenseFile: false,
            licenseFile: null,
            tenantInfo: null,
            isMultiTenancySetup: true,
            isMigrationSetup: false,
            oldTenantUrl: ''
        });
        setErrors({});
    };

    useEffect(() => {
        const fetchUserRequests = async () => {
            try {
                const response = await axios.get('https://qa-tenantbridge.parablu.com/api/requests', {
                    withCredentials: true
                });
                setAllRequests(response.data.requests);
            } catch (error) {
                console.error('Error fetching requests:', error);
            }
        };

        if (userEmail) {
            fetchUserRequests();
        }
    }, [userEmail]);

    // Initialize form data from initialValues if provided (for edit mode)
    useEffect(() => {
        if (initialValues) {
            // Map storage destination from initialValues, handling legacy '2' value
            let storageDest = initialValues.storageDestination || '';
            if (storageDest === '2' && initialValues.azureBlobHosted === true) {
                // Legacy value - map to new '1' (Azure Blob)
                storageDest = '1';
            } else if (storageDest === '2' && initialValues.azureBlobHosted === false) {
                // Could be CPG DataCenter
                storageDest = '2';
            } else if (storageDest === '3' || initialValues.storageDestination === 'BYOS') {
                // BYOS option
                storageDest = '3';
            }

            const mappedData = {
                tenant: initialValues.tenant || '1',
                location: initialValues.location || '1',
                vmSize: initialValues.vmSize || '2',
                cloudName: initialValues.cloudName || '',
                storageDestination: storageDest,
                category: resolveCategoryValue(initialValues.category),
                diskSizeGB: initialValues.diskSizeGB || '512',
                azureBlobHosted: initialValues.azureBlobHosted !== undefined ? initialValues.azureBlobHosted : false,
                azureBlobFuse: initialValues.azureBlobFuse !== undefined ? initialValues.azureBlobFuse : true,
                hasLicenseFile: initialValues.hasLicenseFile !== undefined ? initialValues.hasLicenseFile : false,
                licenseFile: initialValues.licenseFile || null,
                expiryDays: initialValues.expiryDays || 7,
                tenantInfo: initialValues.tenantInfo || null,
                isMultiTenancySetup: initialValues.isMultiTenancySetup !== undefined ? initialValues.isMultiTenancySetup : true,
                isMigrationSetup: initialValues.isMigrationSetup ?? false,
                oldTenantUrl: initialValues.oldTenantUrl || '',
                migrationCompletionDate: initialValues.migrationCompletionDate || '',
                isPOC: initialValues.isPOC || false
            };
            setFormData(mappedData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues]);

    return (
        <>
            {hasAutoProvisionForSelectedCategory && !formData.isMigrationSetup && (
                <Message info>
                    <Message.Header>Auto Provisioning Enabled</Message.Header>
                    <p>
                        Auto provisioning is enabled for this category. Provisioning will begin automatically after you submit the form.
                    </p>
                </Message>
            )}

            {hasAutoProvisionForSelectedCategory && formData.isMigrationSetup && (
                <Message warning>
                    <Message.Header>Auto Provisioning Enabled – DevOps Approval Required</Message.Header>
                    <p>
                        Auto provisioning is enabled for this category. Since this is a migration request, DevOps approval is required. Your request will be submitted and routed to the DevOps team for review and approval.
                    </p>
                </Message>
            )}

            <Form onSubmit={handleSubmit} error={!!errors.submit}>
                {/* Category Field - Now Editable */}
                <Form.Field required>
                    <label>Category</label>
                    {categoryOptions.length === 0 ? (
                        <Message warning>
                            <Message.Header>No Categories Available</Message.Header>
                            <p>You don't have any categories assigned. Please contact an administrator to assign categories to your account.</p>
                        </Message>
                    ) : (
                        <>
                            <Form.Select
                                required
                                name="category"
                                options={categoryOptions}
                                value={formData.category}
                                onChange={handleInputChange}
                                placeholder="Select category"
                            />
                            {formData.category === 'PROD' && (
                                <>
                                    <Form.Checkbox
                                        name="isPOC"
                                        label="Is this tenant for POC?"
                                        checked={formData.isPOC}
                                        onChange={handleInputChange}
                                        style={{ marginTop: '10px' }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </Form.Field>


                {/* VM Location Field - Editable for PROD */}
                <Form.Field>
                    <label>Hosting Location</label>
                    <Form.Select
                        required
                        name="location"
                        options={formData.category === 'PROD'
                            ? locationOptions.filter(loc => ['1', '2', '3', '4'].includes(loc.value)) // Central India, East US, Germany and UAENorth for PROD
                            : formData.category === 'STAGE'
                                ? locationOptions.filter(loc => loc.value === '2') // Only East US for STAGE
                                : formData.category === 'ENGG_QA'
                                    ? locationOptions.filter(loc => ['1', '2'].includes(loc.value)) // Central India and East US for QA
                                    : locationOptions.filter(loc => loc.value === '1') // Only Central India for DEV
                        }
                        value={formData.location}
                        onChange={handleInputChange}
                        disabled={!['PROD', 'ENGG_QA'].includes(formData.category)}
                        style={!['PROD', 'ENGG_QA'].includes(formData.category) ? { opacity: 0.6 } : {}}
                    />
                </Form.Field>

                {/* Setup Type Field - HIDDEN */}
                {/* Keeping the field hidden but maintaining the state value */}

                {/* Storage Destination Field */}
                <Form.Field required>
                    <label>Storage Destination</label>
                    <Form.Select
                        required
                        name="storageDestination"
                        placeholder="Select storage destination"
                        options={storageOptions}
                        value={formData.storageDestination}
                        onChange={handleInputChange}
                        error={errors.storageDestination ? { content: errors.storageDestination } : null}
                    />
                </Form.Field>

                {/* Migration Toggle */}
                <Form.Field>
                    <label>Single Tenant to Multi Tenant Migration?</label>
                    <Form.Radio
                        toggle
                        name="isMigrationSetup"
                        checked={formData.isMigrationSetup}
                        onChange={(e, { checked }) => {
                            setFormData(prev => ({
                                ...prev,
                                isMigrationSetup: checked,
                                oldTenantUrl: checked ? prev.oldTenantUrl : '', // Clear if turning off
                                migrationCompletionDate: checked ? (prev.migrationCompletionDate || '2027-02-25T13:00') : '', // Set default if enabling and empty, clear if turning off
                                cloudName: checked ? '' : prev.cloudName // Clear cloudName when enabling migration
                            }));
                            // Clear errors when toggling
                            setErrors(prev => ({
                                ...prev,
                                oldTenantUrl: '',
                                migrationCompletionDate: '',
                                cloudName: ''
                            }));
                        }}
                        label={formData.isMigrationSetup ? 'Yes' : 'No'}
                    />
                    {formData.isMigrationSetup && (
                        <div style={{
                            marginTop: '0.75rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem'
                        }}>
                            <Icon name="info circle" style={{ color: '#35B7F4', marginTop: '2px', flexShrink: 0 }} />
                            <p style={{ 
                                margin: 0, 
                                fontSize: '0.9em', 
                                color: '#062D4D',
                                lineHeight: '1.5'
                            }}>
                                Single Tenant to Multi Tenant Migration requests require approval from DEVOPS only. Regular approvers cannot approve migration requests.
                            </p>
                        </div>
                    )}
                </Form.Field>

                {/* Conditional Field - Either "Decide your URL" or "Old Tenant URL" */}
                {formData.isMigrationSetup ? (
                    <Form.Field required>
                        <label>Enter your old tenant URL</label>
                        <Form.Input
                            type="text"
                            name="oldTenantUrl"
                            placeholder="https://qa2-portal-in.parablu.com/example"
                            value={formData.oldTenantUrl}
                            onChange={handleInputChange}
                            error={errors.oldTenantUrl ? { content: errors.oldTenantUrl } : null}
                        />
                        <div style={{
                            fontSize: '0.85em',
                            color: '#666',
                            marginTop: '0.5rem',
                            fontStyle: 'italic'
                        }}>
                            Enter the complete HTTPS URL of your old tenant (e.g., https://qa2-portal-in.parablu.com/cloudname). The customer/cloud name will be automatically extracted.
                        </div>
                    </Form.Field>
                ) : null}

                {/* Migration Completion Date/Time - Only shown when migration is enabled */}
                {formData.isMigrationSetup && (
                    <Form.Field>
                        <label>Migration to be completed at or before</label>
                        <Form.Input
                            type="datetime-local"
                            name="migrationCompletionDate"
                            value={formData.migrationCompletionDate || '2027-02-25T13:00'}
                            onChange={handleInputChange}
                            min={new Date().toISOString().slice(0, 16)}
                            style={{ maxWidth: '300px' }}
                            error={errors.migrationCompletionDate ? { content: errors.migrationCompletionDate } : null}
                        />
                    </Form.Field>
                )}

                {!formData.isMigrationSetup && (
                    <Form.Field required>
                        <label>Customer Name</label>
                        <Form.Input
                            type="text"
                            name="cloudName"
                            value={formData.cloudName}
                            onChange={handleInputChange}
                            error={errors.cloudName ? { content: errors.cloudName } : null}
                            disabled={!!initialValues}
                            placeholder="Enter customer name"
                        />
                    </Form.Field>
                )}

                <div className="button-container">
                    <Button type="button" onClick={handleClear} disabled={submitting}>
                        Clear
                    </Button>
                    <Button type="submit" primary loading={submitting} disabled={submitting}>
                        Submit Request
                    </Button>
                </div>
            </Form>

            {/* Auto Provisioning Verification Modal */}
            {hasAutoProvisionForSelectedCategory && (
                <Modal
                    open={showAutoProvisionVerifyModal}
                    onClose={() => {
                        if (!submitting) {
                            setShowAutoProvisionVerifyModal(false);
                            setPendingAutoProvisionData(null);
                        }
                    }}
                    size="small"
                    closeOnDimmerClick={!submitting}
                >
                    <Modal.Header>
                        Verify Details Before Auto Provisioning
                    </Modal.Header>
                    <Modal.Content>
                        <Message info>
                            <Message.Header>Review your request</Message.Header>
                            <p>
                                You have Auto Provisioning enabled for this category. Please verify the key details
                                below. After you confirm, your request will be submitted.
                                {formData.isMigrationSetup ? (
                                    <strong> Note: This is a migration request and requires DEVOPS approval, even with auto provisioning enabled.</strong>
                                ) : (
                                    ' Once you confirm that popup and later approve the request, the creation process will start automatically without any additional approver.'
                                )}
                            </p>
                        </Message>
                        {formData.isMigrationSetup && (
                            <Message warning style={{ marginTop: '1rem' }}>
                                <Message.Header>DEVOPS Approval Required</Message.Header>
                                <p>
                                    This migration request can only be approved by DEVOPS. Regular approvers cannot approve migration requests.
                                </p>
                            </Message>
                        )}
                        <div style={{ marginTop: '1rem' }}>
                            <p><strong>Category:</strong> {formData.category || 'N/A'}</p>
                            <p><strong>Location:</strong> {locationOptions.find(l => l.value === formData.location)?.text || 'N/A'}</p>
                            <p><strong>Customer Name:</strong> {formData.cloudName || 'N/A'}</p>
                            {!formData.isMigrationSetup && (
                                <p><strong>Storage Destination:</strong> {storageOptions.find(s => s.value === formData.storageDestination)?.text || 'N/A'}</p>
                            )}
                            {formData.isMigrationSetup && (
                                <p><strong>Old Tenant URL:</strong> {formData.oldTenantUrl || 'N/A'}</p>
                            )}
                        </div>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            onClick={() => {
                                if (!submitting) {
                                    setShowAutoProvisionVerifyModal(false);
                                    setPendingAutoProvisionData(null);
                                }
                            }}
                            disabled={submitting}
                        >
                            Back to Form
                        </Button>
                        <Button
                            primary
                            onClick={handleConfirmAutoProvisionSubmit}
                            loading={submitting}
                            disabled={submitting}
                        >
                            Confirm & Submit
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}

            <SuccessModal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                roles={roles}
                userEmail={userEmail}
                categories={categories}
                requestCategory={formCategory}
                isMultiTenancy={true}
            />

            <ErrorModal
                open={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                error={submitError}
            />

            <CreationLogsModal
                open={showLogsModal}
                onClose={() => {
                    // Same behavior as ST form: after completion, simply close the modal.
                    // Logs are cleared when a new multi-tenancy provisioning run starts,
                    // so we keep them (and the final elapsed time) intact across re-opens.
                    setShowLogsModal(false);
                }}
                onCancel={() => {
                    if (abortController) {
                        abortController.abort();
                        setCreationLogs(prev => [...prev, `Operation cancelled by ${userEmail}`]);
                        setIsCancelled(true);
                    }
                }}
                isCancelled={isCancelled}
                logs={creationLogs}
            />

            <VMCreationErrorModal
                open={showVMCreationErrorModal}
                onClose={() => {
                    setShowVMCreationErrorModal(false);
                    setVMCreationError(null);
                }}
                {...vmCreationError}
            />
        </>
    );
};

export default RequestForm3;
